/**
 * Saisie par matière (mode tronc commun multi-options).
 *
 * Workflow :
 *  1. L'utilisateur choisit un NOM de matière (groupé par nom_matiere — une
 *     même matière peut exister sur plusieurs options).
 *  2. Verrouillage de la matière. Un mapping "code_classe → matiere_id" est
 *     pré-calculé pour les classes ayant cette matière.
 *  3. Recherche d'un étudiant (nom OU matricule). On filtre côté client pour
 *     ne garder que les étudiants des classes ayant la matière verrouillée.
 *  4. Sélection : la classe est auto-détectée → matiere_id correspondant.
 *  5. Saisie D1 / D2 / AB → Entrée pour sauvegarder + passer au suivant.
 *  6. Liste vivante des saisies récentes (rollback possible).
 */
import React, { useState, useMemo, useEffect, useRef, useCallback } from 'react';
import {
  Lock, Unlock, Search, Save, Loader, CheckCircle2, XCircle,
  ChevronRight, BookOpen, User, Trash2, Edit2, Copy,
} from 'lucide-react';
import { useGetMatieresQuery } from '../../services/matieresApi';
import { useGetEtudiantsQuery } from '../../services/etudiantsApi';
import { useSaisieIndividuelleMutation, useGetNotesDevoirsQuery } from '../../services/notesDevoirsApi';
import { useGetNotesExamensQuery } from '../../services/notesExamensApi';
import type { Matiere } from '../../services/matieresApi';
import type { Etudiant } from '../../services/etudiantsApi';

interface Props {
  anneeAcademique: string;
}

interface SaisieRecente {
  id: string;          // uuid local
  etudiant: Etudiant;
  matiere: Matiere;
  semestre: 'S1' | 'S2';
  note1: number | null;
  absent1: boolean;
  note2: number | null;
  absent2: boolean;
  status: 'pending' | 'success' | 'error';
  message?: string;
  timestamp: number;
}

const SaisieParMatiere: React.FC<Props> = ({ anneeAcademique }) => {
  // ── 1. Sélection matière ──────────────────────────────────────────────────
  const { data: allMatieres = [], isLoading: loadingMatieres } = useGetMatieresQuery();
  const [matiereSearch, setMatiereSearch] = useState('');
  const [selectedNom, setSelectedNom] = useState<string | null>(null);
  const [selectedNiveau, setSelectedNiveau] = useState<string>('');
  const [selectedSemestre, setSelectedSemestre] = useState<'S1' | 'S2'>('S1');
  const [matiereLocked, setMatiereLocked] = useState(false);

  // Note 2026-09-01 v3 : les fenêtres de saisie ne concernent QUE les
  // formateurs (portail formateur). Côté backoffice, les agents/admins
  // saisissent librement — pas de blocage UI ici.

  // Groupement par (nom_matiere + niveau + semestre) — chaque groupe contient
  // les variantes de la matière sur les différentes options/classes
  const groupes = useMemo(() => {
    const map = new Map<string, { nom: string; niveau: string; semestre: number; matieres: Matiere[] }>();
    for (const m of allMatieres) {
      if (!m.actif || !m.nom_matiere) continue;
      const niv = m.niveau || '';
      const sem = m.semestre || 0;
      const key = `${m.nom_matiere}::${niv}::${sem}`;
      if (!map.has(key)) {
        map.set(key, { nom: m.nom_matiere, niveau: niv, semestre: sem, matieres: [] });
      }
      map.get(key)!.matieres.push(m);
    }
    return Array.from(map.values()).sort((a, b) => {
      const c1 = a.niveau.localeCompare(b.niveau);
      if (c1 !== 0) return c1;
      const c2 = a.semestre - b.semestre;
      if (c2 !== 0) return c2;
      return a.nom.localeCompare(b.nom);
    });
  }, [allMatieres]);

  const groupesFiltres = useMemo(() => {
    let res = groupes;
    if (selectedNiveau) res = res.filter(g => g.niveau === selectedNiveau);
    if (selectedSemestre === 'S1') {
      res = res.filter(g => [1, 3, 5].includes(g.semestre));
    } else {
      res = res.filter(g => [2, 4, 6].includes(g.semestre));
    }
    if (matiereSearch.trim()) {
      const q = matiereSearch.trim().toLowerCase();
      res = res.filter(g => g.nom.toLowerCase().includes(q));
    }
    return res.slice(0, 30);
  }, [groupes, selectedNiveau, selectedSemestre, matiereSearch]);

  // Le groupe sélectionné (matière verrouillée)
  const groupeSelectionne = useMemo(() => {
    if (!selectedNom || !selectedNiveau) return null;
    return groupes.find(g =>
      g.nom === selectedNom &&
      g.niveau === selectedNiveau &&
      ((selectedSemestre === 'S1' && [1, 3, 5].includes(g.semestre)) ||
       (selectedSemestre === 'S2' && [2, 4, 6].includes(g.semestre)))
    ) || null;
  }, [selectedNom, selectedNiveau, selectedSemestre, groupes]);

  // Mapping classe → matière correspondante
  const classeVersMatiere = useMemo(() => {
    const map = new Map<string, Matiere>();
    if (!groupeSelectionne) return map;
    for (const m of groupeSelectionne.matieres) {
      if (m.classe) map.set(m.classe.toUpperCase(), m);
    }
    return map;
  }, [groupeSelectionne]);

  // ── 2. Recherche étudiant ─────────────────────────────────────────────────
  const [etudiantSearch, setEtudiantSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const searchInputRef = useRef<HTMLInputElement>(null);

  // Debounce 250ms
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(etudiantSearch.trim()), 250);
    return () => clearTimeout(t);
  }, [etudiantSearch]);

  const { data: etudiantsRecherches = [], isFetching: searching } = useGetEtudiantsQuery(
    { search: debouncedSearch, actif: true, page_size: 30 },
    { skip: !matiereLocked || debouncedSearch.length < 2 }
  );

  // Marquer chaque candidat avec son éligibilité (classe couverte ou non)
  const candidats = useMemo(() => {
    if (!matiereLocked) return [];
    const classesValides = new Set(Array.from(classeVersMatiere.keys()));
    const enriched = etudiantsRecherches.map(e => ({
      etudiant: e,
      eligible: !!(e.classe && classesValides.has(e.classe.toUpperCase())),
    }));
    enriched.sort((a, b) => {
      if (a.eligible !== b.eligible) return a.eligible ? -1 : 1;
      const na = (a.etudiant.nom || '').localeCompare(b.etudiant.nom || '');
      return na;
    });
    return enriched.slice(0, 20);
  }, [etudiantsRecherches, classeVersMatiere, matiereLocked]);

  const nbEligibles = candidats.filter(c => c.eligible).length;

  // ── 3. Étudiant sélectionné + saisie ─────────────────────────────────────
  const [selectedEtu, setSelectedEtu] = useState<Etudiant | null>(null);
  const [note1, setNote1] = useState<string>('');
  const [note2, setNote2] = useState<string>('');
  const [absent1, setAbsent1] = useState(false);
  const [absent2, setAbsent2] = useState(false);
  const note1Ref = useRef<HTMLInputElement>(null);

  const matiereCible = useMemo(() => {
    if (!selectedEtu?.classe) return null;
    return classeVersMatiere.get(selectedEtu.classe.toUpperCase()) || null;
  }, [selectedEtu, classeVersMatiere]);

  // Récupérer les notes existantes pour comparaison (devoir + examen)
  const { data: existingDevoirs = [] } = useGetNotesDevoirsQuery(
    selectedEtu && matiereCible
      ? {
          etudiant_id: selectedEtu.id,
          matiere_id: matiereCible.id,
          annee_academique: anneeAcademique,
          semestre: selectedSemestre,
        }
      : ({} as any),
    { skip: !selectedEtu || !matiereCible }
  );
  const noteDevoirExistante = existingDevoirs.find(n => n.semestre === selectedSemestre);

  const { data: existingExamens = [] } = useGetNotesExamensQuery(
    selectedEtu && matiereCible
      ? {
          etudiant_id: selectedEtu.id,
          matiere_id: matiereCible.id,
          annee_academique: anneeAcademique,
          semestre: selectedSemestre,
          est_rattrapage: false,
        }
      : ({} as any),
    { skip: !selectedEtu || !matiereCible }
  );
  const noteExamenExistante = existingExamens.find(n => n.semestre === selectedSemestre && !n.est_rattrapage);

  // Quand un étudiant est sélectionné, focus auto sur D1
  useEffect(() => {
    if (selectedEtu && note1Ref.current) {
      setTimeout(() => note1Ref.current?.focus(), 50);
    }
  }, [selectedEtu]);

  // ── 4. Mutation + historique ──────────────────────────────────────────────
  const [saisieIndiv, { isLoading: saving }] = useSaisieIndividuelleMutation();
  const [recentes, setRecentes] = useState<SaisieRecente[]>([]);
  const [globalMessage, setGlobalMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  const resetSaisie = () => {
    setSelectedEtu(null);
    setNote1('');
    setNote2('');
    setAbsent1(false);
    setAbsent2(false);
    setEtudiantSearch('');
    setTimeout(() => searchInputRef.current?.focus(), 50);
  };

  const handleSauvegarder = useCallback(async () => {
    if (!selectedEtu || !matiereCible) return;
    const n1 = note1.trim() ? parseFloat(note1) : null;
    const n2 = note2.trim() ? parseFloat(note2) : null;

    // Validation bornes
    for (const [n, label] of [[n1, 'D1'], [n2, 'D2']] as const) {
      if (n !== null && (isNaN(n) || n < 0 || n > 20)) {
        setGlobalMessage({ type: 'error', text: `Note ${label} invalide : ${n} (doit être entre 0 et 20)` });
        setTimeout(() => setGlobalMessage(null), 3000);
        return;
      }
    }
    // Au moins une note ou un absent
    if (n1 === null && !absent1 && n2 === null && !absent2) {
      setGlobalMessage({ type: 'error', text: 'Saisis au moins D1 ou D2 (ou coche AB).' });
      setTimeout(() => setGlobalMessage(null), 3000);
      return;
    }

    const localId = `${selectedEtu.id}_${Date.now()}`;
    const entry: SaisieRecente = {
      id: localId,
      etudiant: selectedEtu,
      matiere: matiereCible,
      semestre: selectedSemestre,
      note1: n1,
      absent1,
      note2: n2,
      absent2,
      status: 'pending',
      timestamp: Date.now(),
    };
    setRecentes(prev => [entry, ...prev].slice(0, 30));

    try {
      await saisieIndiv({
        etudiant_id: selectedEtu.id,
        matiere_id: matiereCible.id,
        annee_academique: anneeAcademique,
        semestre: selectedSemestre,
        note1: absent1 ? null : n1,
        absent1,
        note2: absent2 ? null : n2,
        absent2,
      }).unwrap();
      setRecentes(prev => prev.map(r =>
        r.id === localId ? { ...r, status: 'success', message: 'Sauvegardé' } : r
      ));
      setGlobalMessage({ type: 'success', text: `${selectedEtu.matricule} (${selectedEtu.classe}) — sauvegardé ✓` });
      setTimeout(() => setGlobalMessage(null), 1500);
      resetSaisie();
    } catch (e: any) {
      const msg = e?.data?.error || e?.data?.detail || e?.message || 'Erreur lors de la sauvegarde';
      setRecentes(prev => prev.map(r =>
        r.id === localId ? { ...r, status: 'error', message: msg } : r
      ));
      setGlobalMessage({ type: 'error', text: msg });
    }
  }, [selectedEtu, matiereCible, note1, note2, absent1, absent2, selectedSemestre, anneeAcademique, saisieIndiv]);

  // Gestion clavier : Entrée dans D2 ou D1 (si vide D2) → save
  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleSauvegarder();
    }
  };

  // ── Rendu : pas encore verrouillé ────────────────────────────────────────
  if (!matiereLocked || !groupeSelectionne) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6 max-w-3xl mx-auto">
        <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-1 flex items-center gap-2">
          <BookOpen className="text-blue-600" size={22} />
          Saisie par matière
        </h2>
        <p className="text-sm text-gray-600 dark:text-gray-400 mb-5">
          Choisis une matière pour la verrouiller, puis saisis les notes étudiant par étudiant — quelle que soit leur classe.
          Idéal pour les matières en tronc commun (un même formateur, plusieurs options).
        </p>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-4">
          <div>
            <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Niveau</label>
            <select
              value={selectedNiveau}
              onChange={(e) => setSelectedNiveau(e.target.value)}
              className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700"
            >
              <option value="">— Choisir —</option>
              {['L1', 'L2', 'L3', 'M1', 'M2'].map(n => <option key={n} value={n}>{n}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Semestre</label>
            <select
              value={selectedSemestre}
              onChange={(e) => setSelectedSemestre(e.target.value as 'S1' | 'S2')}
              className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700"
            >
              <option value="S1">Semestre 1</option>
              <option value="S2">Semestre 2</option>
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Année</label>
            <input
              type="text"
              value={anneeAcademique}
              readOnly
              className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded bg-gray-50 dark:bg-gray-900 text-gray-700"
            />
          </div>
        </div>

        <div className="mb-3">
          <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
            Rechercher la matière
          </label>
          <div className="relative">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              value={matiereSearch}
              onChange={(e) => setMatiereSearch(e.target.value)}
              placeholder="Ex: Mathématiques générales, Anglais…"
              disabled={!selectedNiveau}
              className="w-full pl-9 pr-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 disabled:bg-gray-100 dark:disabled:bg-gray-900"
            />
          </div>
        </div>

        {selectedNiveau && (
          <div className="border border-gray-200 dark:border-gray-700 rounded max-h-80 overflow-y-auto">
            {loadingMatieres ? (
              <div className="text-center py-6"><Loader className="animate-spin mx-auto" size={24} /></div>
            ) : groupesFiltres.length === 0 ? (
              <div className="text-center py-6 text-sm text-gray-500">
                Aucune matière trouvée pour {selectedNiveau} {selectedSemestre}.
              </div>
            ) : (
              groupesFiltres.map(g => (
                <button
                  key={`${g.nom}::${g.niveau}::${g.semestre}`}
                  onClick={() => {
                    setSelectedNom(g.nom);
                    setMatiereLocked(true);
                  }}
                  className="w-full text-left px-3 py-2 border-b border-gray-100 dark:border-gray-700 hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors"
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="font-medium text-gray-900 dark:text-gray-100">{g.nom}</div>
                      <div className="text-xs text-gray-500 dark:text-gray-400">
                        {g.niveau} S{g.semestre} · {g.matieres.length} option{g.matieres.length > 1 ? 's' : ''}
                        {' · '}
                        {g.matieres.map(m => m.classe).filter(Boolean).slice(0, 5).join(', ')}
                        {g.matieres.length > 5 && ', …'}
                      </div>
                    </div>
                    <ChevronRight className="text-gray-400" size={16} />
                  </div>
                </button>
              ))
            )}
          </div>
        )}

        {!selectedNiveau && (
          <div className="text-sm text-gray-500 dark:text-gray-400 text-center py-6">
            Choisis d'abord un niveau.
          </div>
        )}
      </div>
    );
  }

  // ── Rendu : verrouillé, en saisie ────────────────────────────────────────
  return (
    <div className="max-w-screen-xl mx-auto space-y-3">
      {/* En-tête : matière verrouillée */}
      <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-700 rounded-lg p-3 flex items-center gap-3 flex-wrap">
        <Lock className="text-blue-600" size={20} />
        <div className="flex-1 min-w-[200px]">
          <div className="text-sm font-bold text-blue-900 dark:text-blue-100">
            {groupeSelectionne.nom}
          </div>
          <div className="text-xs text-blue-700 dark:text-blue-300">
            {groupeSelectionne.niveau} {selectedSemestre} · {groupeSelectionne.matieres.length} option(s) couverte(s) :{' '}
            {Array.from(classeVersMatiere.keys()).join(', ')}
          </div>
        </div>
        <div className="flex items-center gap-2 text-xs text-blue-700 dark:text-blue-300">
          <span>{anneeAcademique}</span>
          <button
            onClick={() => {
              setMatiereLocked(false);
              setSelectedNom(null);
              setSelectedEtu(null);
              setEtudiantSearch('');
              setRecentes([]);
            }}
            className="flex items-center gap-1 px-2 py-1 bg-white dark:bg-gray-700 border border-blue-300 dark:border-blue-600 rounded hover:bg-blue-100"
            title="Déverrouiller pour changer de matière"
          >
            <Unlock size={12} /> Changer
          </button>
        </div>
      </div>

      {/* Barre de saisie : recherche étudiant + champs notes */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4 grid grid-cols-1 lg:grid-cols-[1fr_auto] gap-3 items-start">
        {/* Recherche / sélection étudiant */}
        <div>
          <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
            Étudiant (matricule ou nom)
          </label>
          {selectedEtu ? (
            <div className="p-2 border border-blue-300 bg-blue-50 dark:bg-blue-900/30 rounded space-y-2">
              <div className="flex items-center gap-2">
                <User size={16} className="text-blue-600 shrink-0" />
                <div className="flex-1">
                  <div className="font-mono font-bold text-sm text-gray-900 dark:text-gray-100">
                    {selectedEtu.matricule}
                  </div>
                  <div className="text-sm text-gray-700 dark:text-gray-300">
                    {selectedEtu.nom} {selectedEtu.prenoms}
                  </div>
                  <div className="text-xs text-gray-500 dark:text-gray-400">
                    Classe : <span className="font-semibold text-blue-700 dark:text-blue-300">{selectedEtu.classe}</span>
                    {' · matière_id : '}
                    <span className="font-mono text-[10px]">{matiereCible?.id} ({matiereCible?.code_matiere})</span>
                  </div>
                </div>
                <button
                  onClick={resetSaisie}
                  className="text-xs text-gray-500 hover:text-gray-700 px-2"
                  title="Retirer l'étudiant et chercher un autre"
                >
                  <XCircle size={16} />
                </button>
              </div>

              {/* Indicateur : notes existantes pour cet étudiant + matière + semestre */}
              {(noteDevoirExistante || noteExamenExistante) && (
                <div className="bg-amber-50 dark:bg-amber-900/30 border border-amber-200 dark:border-amber-700 rounded p-1.5 text-[11px] flex items-center gap-3 flex-wrap">
                  <span className="font-semibold text-amber-800 dark:text-amber-300">
                    📋 Notes déjà saisies (à comparer) :
                  </span>
                  {noteDevoirExistante && (
                    <>
                      <span className="text-blue-700 dark:text-blue-300">
                        D1 :{' '}
                        <strong>
                          {noteDevoirExistante.absent1
                            ? 'AB'
                            : (noteDevoirExistante.note1 != null ? noteDevoirExistante.note1 : '—')}
                        </strong>
                      </span>
                      <span className="text-purple-700 dark:text-purple-300">
                        D2 :{' '}
                        <strong>
                          {noteDevoirExistante.absent2
                            ? 'AB'
                            : (noteDevoirExistante.note2 != null ? noteDevoirExistante.note2 : '—')}
                        </strong>
                      </span>
                      {noteDevoirExistante.moyenne_devoirs && (
                        <span className="text-gray-700 dark:text-gray-300">
                          Moy : <strong>{noteDevoirExistante.moyenne_devoirs}</strong>
                        </span>
                      )}
                    </>
                  )}
                  {noteExamenExistante && (
                    <span className="text-emerald-700 dark:text-emerald-300">
                      SO :{' '}
                      <strong>
                        {noteExamenExistante.absent
                          ? 'AB'
                          : (noteExamenExistante.note_examen != null ? noteExamenExistante.note_examen : '—')}
                      </strong>
                    </span>
                  )}
                  {noteDevoirExistante && (
                    <button
                      onClick={() => {
                        if (noteDevoirExistante.absent1) {
                          setAbsent1(true); setNote1('');
                        } else if (noteDevoirExistante.note1 != null) {
                          setNote1(String(noteDevoirExistante.note1)); setAbsent1(false);
                        }
                        if (noteDevoirExistante.absent2) {
                          setAbsent2(true); setNote2('');
                        } else if (noteDevoirExistante.note2 != null) {
                          setNote2(String(noteDevoirExistante.note2)); setAbsent2(false);
                        }
                      }}
                      title="Reprendre les valeurs existantes dans les champs (pour modification)"
                      className="ml-auto px-1.5 py-0.5 text-[10px] bg-white dark:bg-gray-700 border border-amber-300 dark:border-amber-700 text-amber-700 dark:text-amber-300 rounded hover:bg-amber-100 dark:hover:bg-amber-800/40"
                    >
                      Reprendre
                    </button>
                  )}
                </div>
              )}
            </div>
          ) : (
            <div>
              <div className="relative">
                <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <input
                  ref={searchInputRef}
                  type="text"
                  value={etudiantSearch}
                  onChange={(e) => setEtudiantSearch(e.target.value)}
                  placeholder="Tape le nom ou matricule (au moins 2 caractères)…"
                  autoFocus
                  className="w-full pl-9 pr-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700"
                />
                {searching && (
                  <Loader size={14} className="absolute right-3 top-1/2 -translate-y-1/2 animate-spin text-gray-400" />
                )}
              </div>
              {debouncedSearch.length >= 2 && (
                <div className="mt-1 border border-gray-200 dark:border-gray-700 rounded max-h-60 overflow-y-auto bg-white dark:bg-gray-700">
                  {candidats.length === 0 ? (
                    <div className="text-center text-xs text-gray-500 py-3 px-2">
                      {searching ? 'Recherche en cours…' : 'Aucun étudiant trouvé pour cette recherche.'}
                    </div>
                  ) : (
                    <>
                      {nbEligibles === 0 && (
                        <div className="px-3 py-1.5 bg-amber-50 dark:bg-amber-900/30 text-[10px] text-amber-700 dark:text-amber-300 border-b border-amber-200 dark:border-amber-700">
                          ⚠ {candidats.length} étudiant(s) trouvé(s) mais aucun dans une classe couverte par <strong>{groupeSelectionne.nom}</strong>.
                        </div>
                      )}
                      {candidats.map(({ etudiant: e, eligible }) => (
                        <button
                          key={e.id}
                          onClick={() => eligible && setSelectedEtu(e)}
                          disabled={!eligible}
                          title={eligible
                            ? `Cliquer pour saisir D1/D2 pour ${e.nom} ${e.prenoms}`
                            : `Classe ${e.classe || '?'} non couverte par la matière verrouillée`}
                          className={`w-full text-left px-3 py-2 border-b last:border-b-0 border-gray-100 dark:border-gray-600 transition-colors ${
                            eligible
                              ? 'hover:bg-blue-50 dark:hover:bg-blue-900/20 cursor-pointer'
                              : 'opacity-50 cursor-not-allowed bg-gray-50 dark:bg-gray-800/40'
                          }`}
                        >
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="font-mono font-semibold text-sm text-gray-900 dark:text-gray-100">
                              {e.matricule}
                            </span>
                            <span className="text-sm text-gray-700 dark:text-gray-300">
                              {e.nom} {e.prenoms}
                            </span>
                            <span className={`ml-auto text-[10px] px-1.5 py-0.5 rounded font-semibold ${
                              eligible
                                ? 'bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300'
                                : 'bg-gray-200 dark:bg-gray-700 text-gray-600 dark:text-gray-400 line-through'
                            }`}>
                              {e.classe || '?'}
                            </span>
                            {!eligible && (
                              <span className="text-[10px] text-amber-600 dark:text-amber-400 italic">
                                (hors scope)
                              </span>
                            )}
                          </div>
                        </button>
                      ))}
                    </>
                  )}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Notes D1 / D2 */}
        <div className="flex items-end gap-2">
          <div>
            <label className="block text-xs font-medium text-blue-700 mb-1">D1 <span className="text-[10px] text-gray-500 font-normal">— vide = 0</span></label>
            <input
              ref={note1Ref}
              type="number" min="0" max="20" step="0.25"
              value={note1}
              onChange={(e) => setNote1(e.target.value)}
              onBlur={(e) => { if (e.target.value.trim() === '' && selectedEtu) setNote1('0'); }}
              onKeyDown={handleKeyDown}
              disabled={!selectedEtu}
              placeholder="—"
              className="w-20 px-2 py-2 text-base font-bold text-center border border-blue-300 dark:border-blue-700 rounded bg-white dark:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-400 disabled:bg-gray-100 dark:disabled:bg-gray-900"
            />
          </div>

          {/* Bouton "Copier D1 → D2" : utile quand le formateur n'a fait qu'un
              seul devoir et veut le reporter sur D2 d'un clic. */}
          <button
            onClick={() => {
              if (!selectedEtu) return;
              if (note1.trim()) {
                setNote2(note1);
              }
            }}
            disabled={!selectedEtu || !note1.trim()}
            title="Copier la note D1 dans D2 (utile quand un seul devoir a été fait)"
            className="flex items-center gap-1 px-2 py-2 mb-[2px] bg-gray-100 hover:bg-blue-100 dark:bg-gray-700 dark:hover:bg-blue-900/40 text-gray-700 dark:text-gray-200 border border-gray-300 dark:border-gray-600 rounded text-xs font-medium disabled:opacity-40 disabled:cursor-not-allowed self-end"
          >
            <Copy size={12} /> D1→D2
          </button>

          <div>
            <label className="block text-xs font-medium text-purple-700 mb-1">D2 <span className="text-[10px] text-gray-500 font-normal">— vide = 0</span></label>
            <input
              type="number" min="0" max="20" step="0.25"
              value={note2}
              onChange={(e) => setNote2(e.target.value)}
              onBlur={(e) => { if (e.target.value.trim() === '' && selectedEtu) setNote2('0'); }}
              onKeyDown={handleKeyDown}
              disabled={!selectedEtu}
              placeholder="—"
              className="w-20 px-2 py-2 text-base font-bold text-center border border-purple-300 dark:border-purple-700 rounded bg-white dark:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-purple-400 disabled:bg-gray-100 dark:disabled:bg-gray-900"
            />
          </div>

          <button
            onClick={handleSauvegarder}
            disabled={!selectedEtu || saving}
            className="flex items-center gap-1 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded text-sm font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
            title="Entrée pour sauvegarder"
          >
            {saving ? <Loader size={14} className="animate-spin" /> : <Save size={14} />}
            Sauvegarder
          </button>
        </div>
      </div>

      {/* Message global */}
      {globalMessage && (
        <div className={`p-2 rounded text-sm flex items-center gap-2 ${
          globalMessage.type === 'success'
            ? 'bg-green-50 dark:bg-green-900/30 text-green-700 dark:text-green-300 border border-green-200 dark:border-green-700'
            : 'bg-red-50 dark:bg-red-900/30 text-red-700 dark:text-red-300 border border-red-200 dark:border-red-700'
        }`}>
          {globalMessage.type === 'success' ? <CheckCircle2 size={14} /> : <XCircle size={14} />}
          {globalMessage.text}
        </div>
      )}

      {/* Saisies récentes */}
      {recentes.length > 0 && (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow">
          <div className="px-4 py-2 border-b dark:border-gray-700 flex items-center justify-between">
            <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
              Saisies récentes ({recentes.length})
            </h3>
            <button
              onClick={() => setRecentes([])}
              className="text-xs text-gray-500 hover:text-red-600 flex items-center gap-1"
            >
              <Trash2 size={11} /> Effacer la liste
            </button>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead className="bg-gray-50 dark:bg-gray-900/40 text-gray-600 dark:text-gray-400">
                <tr>
                  <th className="text-left px-3 py-1.5 font-medium">Heure</th>
                  <th className="text-left px-3 py-1.5 font-medium">Matricule</th>
                  <th className="text-left px-3 py-1.5 font-medium">Nom</th>
                  <th className="text-left px-3 py-1.5 font-medium">Classe</th>
                  <th className="text-center px-3 py-1.5 font-medium">D1</th>
                  <th className="text-center px-3 py-1.5 font-medium">D2</th>
                  <th className="text-left px-3 py-1.5 font-medium">Statut</th>
                </tr>
              </thead>
              <tbody>
                {recentes.map(r => (
                  <tr key={r.id} className="border-t border-gray-100 dark:border-gray-700">
                    <td className="px-3 py-1.5 text-gray-500 font-mono">
                      {new Date(r.timestamp).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
                    </td>
                    <td className="px-3 py-1.5 font-mono font-semibold">{r.etudiant.matricule}</td>
                    <td className="px-3 py-1.5">{r.etudiant.nom} {r.etudiant.prenoms}</td>
                    <td className="px-3 py-1.5">
                      <span className="text-[10px] px-1.5 py-0.5 bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 rounded">
                        {r.etudiant.classe}
                      </span>
                    </td>
                    <td className="px-3 py-1.5 text-center font-bold">
                      {r.absent1 ? <span className="text-orange-600">AB</span> : (r.note1 != null ? r.note1.toFixed(2) : '—')}
                    </td>
                    <td className="px-3 py-1.5 text-center font-bold">
                      {r.absent2 ? <span className="text-orange-600">AB</span> : (r.note2 != null ? r.note2.toFixed(2) : '—')}
                    </td>
                    <td className="px-3 py-1.5">
                      {r.status === 'pending' && <span className="text-gray-500 flex items-center gap-1"><Loader size={11} className="animate-spin" /> En cours…</span>}
                      {r.status === 'success' && <span className="text-green-600 flex items-center gap-1"><CheckCircle2 size={11} /> {r.message}</span>}
                      {r.status === 'error' && <span className="text-red-600 flex items-center gap-1" title={r.message}><XCircle size={11} /> Erreur</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Astuce clavier */}
      <div className="text-[11px] text-gray-500 dark:text-gray-400 text-center">
        💡 Astuce : tape le matricule ou le nom, sélectionne dans la liste, saisis D1 / D2, et appuie sur <kbd className="px-1.5 py-0.5 bg-gray-200 dark:bg-gray-700 rounded font-mono">Entrée</kbd> pour sauvegarder et passer au suivant.
      </div>
    </div>
  );
};

export default SaisieParMatiere;
